import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Box,
  Paper,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Snackbar,
  Alert,
  Button,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { saveTournament, getTournament } from '../../../services/tournament.service';
import {
  TOURNAMENT_TYPE,
  TOURNAMENT_SISTEMA,
  TOURNAMENT_RITMO,
  TOURNAMENT_ESTADO,
  TOURNAMENT_SOFTWARE,
} from '../../../constants/tournament.constants';

import StepGeneralData from './StepGeneralData';
import StepNormalPlayers from './StepNormalPlayers';
import StepTeamPlayers from './StepTeamPlayers';
import StepNormalRoundsMatches from './StepNormalRoundsMatches';
import StepTeamRoundsMatches from './StepTeamRoundsMatches';
import StepStandings from './StepStandings';
import StepTeamStandings from './StepTeamStandings';

const stepsNormal = [
  'Datos generales del torneo',
  'Jugadores del torneo',
  'Partidas',
  'Cálculo de puntos',
];

const stepsTeams = [
  'Datos generales del torneo',
  'Equipos / Jugadores por equipo',
  'Enfrentamientos y Partidas',
  'Cálculo de puntos',
];

export default function TournamentCreateWizard() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { torIdParam } = useParams();

  const [activeStep, setActiveStep] = useState(0);
  const [torId, setTorId] = useState(torIdParam ? Number(torIdParam) : null);
  const [dataLoading, setDataLoading] = useState(!!torIdParam);

  // Form State Backup (so that if user goes back, their inputs are preserved)
  const [formData, setFormData] = useState({
    nombre: '',
    organizador: '',
    lugar: '',
    arbitro: '',
    tipo: TOURNAMENT_TYPE.REGULAR_ABIERTO, // Will be set dynamically by useEffect or loaded
    sistema: TOURNAMENT_SISTEMA.SUIZO,
    ritmoJuego: TOURNAMENT_RITMO.PENSADO,
    nroRondas: 6,
    tipoPuntaje: 1,  // 1=Match Points (default para equipos)
    nroTableros: 4,  // default para equipos
    rankeado: 'S',
    fechaInicio: new Date().toISOString().split('T')[0],
    estado: TOURNAMENT_ESTADO.CREADO,
    comentarios: '',
    softwareUsado: TOURNAMENT_SOFTWARE.MANUAL,
  });

  const [lastSavedData, setLastSavedData] = useState(null);
  const [alertInfo, setAlertInfo] = useState({ open: false, message: '', severity: 'info' });

  // Initialize tournament type correctly if it's new
  useEffect(() => {
    if (!torIdParam) {
      const isTeams = searchParams.get('type') === 'teams';
      setFormData((prev) => ({ ...prev, tipo: isTeams ? TOURNAMENT_TYPE.EQUIPOS : TOURNAMENT_TYPE.REGULAR_ABIERTO }));
    }
  }, [torIdParam, searchParams]);

  useEffect(() => {
    if (!torIdParam) return;
    const loadTournamentData = async () => {
      setDataLoading(true);
      try {
        const data = await getTournament(torIdParam);
        if (data) {
          const mappedData = {
            nombre: data.tor_nombre || '',
            organizador: data.tor_organizador || '',
            lugar: data.tor_lugar || '',
            arbitro: data.tor_arbitro || '',
            tipo: Number(data.tor_tipo ?? TOURNAMENT_TYPE.REGULAR_ABIERTO),
            sistema: Number(data.tor_sistema ?? TOURNAMENT_SISTEMA.SUIZO),
            ritmoJuego: Number(data.tor_ritmo_juego ?? TOURNAMENT_RITMO.PENSADO),
            nroRondas: Number(data.tor_rondas ?? 6),
            tipoPuntaje: Number(data.tor_tipo_puntaje ?? 1),
            nroTableros: Number(data.tor_nro_tableros ?? 4),
            rankeado: data.tor_rankeado || 'S',
            fechaInicio: data.tor_fecha_inicio ? data.tor_fecha_inicio.split('T')[0] : new Date().toISOString().split('T')[0],
            estado: Number(data.tor_estado ?? TOURNAMENT_ESTADO.CREADO),
            comentarios: data.tor_comentarios || '',
            softwareUsado: Number(data.tor_software_usado ?? TOURNAMENT_SOFTWARE.MANUAL),
          };
          setFormData(mappedData);
          setLastSavedData(mappedData);
        }
      } catch (error) {
        console.error('Error al cargar datos del torneo para edición:', error);
        setAlertInfo({
          open: true,
          message: 'Error al cargar los datos del torneo.',
          severity: 'error',
        });
      } finally {
        setDataLoading(false);
      }
    };
    loadTournamentData();
  }, [torIdParam]);

  const type = formData.tipo === TOURNAMENT_TYPE.EQUIPOS ? 'teams' : (searchParams.get('type') || 'normal');
  const steps = type === 'teams' ? stepsTeams : stepsNormal;

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  if (dataLoading) {
    return (
      <Container maxWidth="lg" sx={{ pt: 8, pb: 8, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ pt: 1, pb: 4 }}>
      {/* Botón de Regresar */}
      <Box sx={{ mb: 1 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/tournaments')}
          sx={{ color: '#94a3b8', borderRadius: 0, '&:hover': { color: '#cbd5e1' } }}
        >
          Volver a Torneos
        </Button>
      </Box>

      {/* Título Principal */}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="h4" component="h1" sx={{ color: '#cbd5e1', fontWeight: 'bold' }}>
          {torIdParam ? 'Editar Torneo' : 'Crear Nuevo Torneo'} {type === 'teams' ? '(Por Equipos)' : '(Normal)'}
        </Typography>
      </Box>

      {/* Stepper horizontal personalizado */}
      <Box sx={{ width: '100%', mb: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label, index) => (
            <Step key={label} disabled={index > activeStep}>
              <StepLabel
                StepIconProps={{
                  sx: {
                    '&.Mui-active': { color: '#3a6eb6' },
                    '&.Mui-completed': { color: '#10b981' },
                    '&.Mui-disabled': { color: '#334155' },
                  },
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: index === activeStep ? '#3a6eb6' : index < activeStep ? '#10b981' : '#64748b',
                    fontWeight: index === activeStep ? 'bold' : 'normal',
                  }}
                >
                  {label}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Formulario / Contenedor principal con Glassmorphism */}
      <Paper
        sx={{
          p: 3,
          background: 'rgba(30, 41, 59, 0.7)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
          borderRadius: 0,
          '& .MuiOutlinedInput-root': {
            borderRadius: 0,
          },
          '& .MuiOutlinedInput-root fieldset': {
            borderRadius: 0,
          },
          '& .MuiButton-root': {
            borderRadius: 0,
          },
          '& .MuiAlert-root': {
            borderRadius: 0,
          },
        }}
      >
        {activeStep === 0 && (
          <StepGeneralData
            torId={torId}
            initialFormData={formData}
            lastSavedData={lastSavedData}
            onSaveSuccess={(newTorId, savedData) => {
              setTorId(newTorId);
              setFormData(savedData);
              setLastSavedData(savedData);
              setActiveStep(1);
            }}
            onNextWithoutSaving={() => setActiveStep(1)}
            setAlertInfo={setAlertInfo}
            type={type}
          />
        )}

        {activeStep === 1 && type !== 'teams' && (
          <StepNormalPlayers
            torId={torId}
            onNext={() => setActiveStep(2)}
            onBack={() => handleBack()}
            setAlertInfo={setAlertInfo}
          />
        )}

        {activeStep === 1 && type === 'teams' && (
          <StepTeamPlayers
            torId={torId}
            onNext={() => setActiveStep(2)}
            onBack={() => handleBack()}
            setAlertInfo={setAlertInfo}
          />
        )}

        {activeStep === 2 && type !== 'teams' && (
          <StepNormalRoundsMatches
            torId={torId}
            onNext={() => setActiveStep(3)}
            onBack={() => handleBack()}
            setAlertInfo={setAlertInfo}
            nroRondas={formData.nroRondas}
            onUpdateTournamentRondas={async (newTotalRondas) => {
              const updatedFormData = { ...formData, nroRondas: newTotalRondas };
              setFormData(updatedFormData);
              setLastSavedData(updatedFormData);
              await saveTournament({ ...updatedFormData, torId });
            }}
          />
        )}

        {activeStep === 2 && type === 'teams' && (
          <StepTeamRoundsMatches
            torId={torId}
            onNext={() => setActiveStep(3)}
            onBack={() => handleBack()}
            setAlertInfo={setAlertInfo}
            nroRondas={formData.nroRondas}
            onUpdateTournamentRondas={async (newTotalRondas) => {
              const updatedFormData = { ...formData, nroRondas: newTotalRondas };
              setFormData(updatedFormData);
              setLastSavedData(updatedFormData);
              await saveTournament({ ...updatedFormData, torId });
            }}
          />
        )}

        {activeStep === 3 && type !== 'teams' && (
          <StepStandings
            torId={torId}
            onBack={() => handleBack()}
            setAlertInfo={setAlertInfo}
          />
        )}

        {activeStep === 3 && type === 'teams' && (
          <StepTeamStandings
            torId={torId}
            tipoPuntaje={formData.tipoPuntaje}
            onBack={() => handleBack()}
            setAlertInfo={setAlertInfo}
          />
        )}
      </Paper>

      {/* Alertas emergentes (Toast) */}
      <Snackbar
        open={alertInfo.open}
        autoHideDuration={4000}
        onClose={() => setAlertInfo((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setAlertInfo((prev) => ({ ...prev, open: false }))}
          severity={alertInfo.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: 0 }}
        >
          {alertInfo.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
